import { Button, Icon, Menu, MenuItem, MenuSeparator } from '@tempots/beatui'
import { attr, Fragment, html, prop, Signal, TNode } from '@tempots/dom'
import { NavigationService } from '@tempots/ui'
import { UserMenu } from './user-menu'
import { AgencySelector } from './agency-selector'
import { TopBar, TopNavLinks } from './top-bar'
import { AgencyService, type AgencyRole } from '@/services/agency-service'

export interface AppLayoutOptions {
  agencyId: Signal<string>
  /** Page-specific actions shown on the right side of the top bar. */
  topBarContent?: TNode
  /** Main content pane (left pane in 2-pane layout, only pane otherwise) */
  content: TNode
  /** Optional right pane for 2-pane layout (e.g., preview) */
  rightPane?: TNode
}

/**
 * Unified app layout: a single top bar (logo, section nav, contextual
 * actions, agency selector, user menu) above full-width content.
 *
 * Without rightPane: content fills the whole area.
 * With rightPane:
 * - Below lg: content and rightPane stacked vertically
 * - lg+: content and rightPane side-by-side
 */
export function AppLayout({
  agencyId,
  topBarContent,
  content,
  rightPane,
}: AppLayoutOptions) {
  const is2Pane = rightPane !== undefined

  // The current user's role in the active agency; drives which nav entries
  // (Customize) are visible. Null until loaded — treated as no access.
  const myRole = prop<AgencyRole | null>(null)
  agencyId.on(id => {
    void AgencyService.getMyAgencyRole(id).then(role => myRole.set(role))
  })
  const canCustomize = myRole.map(AgencyService.canManageTemplate)

  return html.div(
    attr.class('flex flex-col h-full overflow-hidden'),
    TopBar({
      logoHref: agencyId.map(id => `/${id}/workspace`),
      // Hamburger menu - replicates nav + agency selector on mobile (< md)
      menu: Button(
        { variant: 'outline' },
        Icon({ icon: 'line-md:menu', color: 'gray' }),
        Menu({
          items: () => [
            MenuItem({
              content: AgencySelector({ agencyId }),
            }),
            MenuSeparator(),
            MenuItem({
              onClick: () =>
                NavigationService.navigate(`/${agencyId.value}/workspace`),
              content: 'Workspace',
            }),
            ...(canCustomize.value
              ? [
                  MenuItem({
                    onClick: () =>
                      NavigationService.navigate(
                        `/${agencyId.value}/customize`
                      ),
                    content: 'Customize',
                  }),
                ]
              : []),
            MenuItem({
              onClick: () =>
                NavigationService.navigate(`/${agencyId.value}/agency`),
              content: 'Agency',
            }),
          ],
        })
      ),
      nav: TopNavLinks({ agencyId, canCustomize }),
      end: Fragment(
        topBarContent ?? null,
        html.div(attr.class('hidden md:block'), AgencySelector({ agencyId })),
        UserMenu({ agencyId })
      ),
    }),
    // Content area - single pane, or content + right pane
    is2Pane
      ? html.div(
          attr.class(
            'grid grid-cols-1 grid-rows-[auto_1fr] lg:grid-cols-[2fr_3fr] lg:grid-rows-1 h-full overflow-hidden flex-grow gap-0'
          ),
          html.div(
            attr.class(
              'overflow-hidden flex flex-col min-h-0 max-h-80 lg:max-h-full'
            ),
            content
          ),
          html.div(
            attr.class('overflow-hidden flex flex-col min-h-0'),
            rightPane
          )
        )
      : html.div(
          attr.class('overflow-hidden flex flex-col min-h-0 flex-grow'),
          content
        )
  )
}
